import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { calculateFlowScore } from '../utils/scoring';

const { FiTrendingUp, FiTarget, FiZap, FiRefreshCw, FiDownload, FiPhone } = FiIcons;

const Results = ({ answers, userInfo, onRestart }) => {
  const results = calculateFlowScore(answers);

  const getScoreColor = (score) => {
    if (score >= 4) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 3) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getScoreDescription = (score) => {
    if (score >= 4) return 'Hoch';
    if (score >= 3) return 'Mittel';
    return 'Niedrig';
  };

  const getInterpretation = (score) => {
    if (score >= 4) {
      return 'Sie haben ein starkes Flow-Erleben während der Tätigkeit erfahren. Dies deutet auf optimale Herausforderung und hohe Konzentration hin.';
    }
    if (score >= 3) {
      return 'Sie haben ein moderates Flow-Erleben erfahren. Die Tätigkeit war größtenteils ansprechend und fesselnd.';
    }
    return 'Das Flow-Erleben war eher gering. Möglicherweise war die Tätigkeit zu einfach, zu schwer oder nicht interessant genug.';
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white rounded-2xl shadow-xl p-8 md:p-12"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full mb-6"
          >
            <SafeIcon icon={FiTrendingUp} className="text-3xl text-white" />
          </motion.div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Ihre Ergebnisse
          </h1>
          <p className="text-xl text-gray-600">
            Flow-Erleben bei: {userInfo.activity}
          </p>
        </div>

        {/* Overall Score */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={`rounded-xl border-2 p-6 mb-8 ${getScoreColor(results.totalScore)}`}
        >
          <div className="text-center">
            <div className="text-4xl font-bold mb-2">
              {results.totalScore.toFixed(1)}
            </div>
            <div className="text-lg font-semibold mb-2">
              Gesamt-Flow-Score ({getScoreDescription(results.totalScore)})
            </div>
            <div className="text-sm opacity-80">
              von 5.0 möglichen Punkten
            </div>
          </div>
        </motion.div>

        {/* Subscale Scores */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-blue-50 rounded-xl p-6 border border-blue-200"
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiTarget} className="text-2xl text-blue-600 mr-3" />
              <h3 className="font-semibold text-gray-900">Absorption</h3>
            </div>
            <div className="text-2xl font-bold text-blue-600 mb-2">
              {results.absorption.toFixed(1)}
            </div>
            <p className="text-sm text-gray-600">
              Versunkenheit und Konzentration in die Tätigkeit
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-purple-50 rounded-xl p-6 border border-purple-200"
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiZap} className="text-2xl text-purple-600 mr-3" />
              <h3 className="font-semibold text-gray-900">Flüssigkeit</h3>
            </div>
            <div className="text-2xl font-bold text-purple-600 mb-2">
              {results.fluency.toFixed(1)}
            </div>
            <p className="text-sm text-gray-600">
              Automatische und mühelose Ausführung
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-green-50 rounded-xl p-6 border border-green-200"
          >
            <div className="flex items-center mb-4">
              <SafeIcon icon={FiTarget} className="text-2xl text-green-600 mr-3" />
              <h3 className="font-semibold text-gray-900">Besorgnis</h3>
            </div>
            <div className="text-2xl font-bold text-green-600 mb-2">
              {results.worry.toFixed(1)}
            </div>
            <p className="text-sm text-gray-600">
              Sorgen und Ängste während der Tätigkeit (niedrig = besser)
            </p>
          </motion.div>
        </div>

        {/* Interpretation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-gray-50 rounded-xl p-6 mb-8"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Interpretation Ihrer Ergebnisse
          </h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            {getInterpretation(results.totalScore)}
          </p>
          
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
              <span><strong>Hoch (4.0-5.0):</strong> Starkes Flow-Erleben, optimale Bedingungen</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
              <span><strong>Mittel (3.0-3.9):</strong> Moderates Flow-Erleben, gute Bedingungen</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
              <span><strong>Niedrig (1.0-2.9):</strong> Geringes Flow-Erleben, Verbesserungspotential</span>
            </div>
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-4 mb-8"
        >
          <button
            onClick={onRestart}
            className="flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <SafeIcon icon={FiRefreshCw} className="mr-2" />
            Neues Assessment
          </button>
          
          <button
            onClick={() => window.print()}
            className="flex items-center px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-gray-400 hover:bg-gray-50 transition-all duration-200"
          >
            <SafeIcon icon={FiDownload} className="mr-2" />
            Ergebnisse drucken
          </button>
        </motion.div>

        {/* Professional Service Offer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.85 }}
          className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl text-center"
        >
          <div className="flex flex-col items-center justify-center">
            <SafeIcon icon={FiPhone} className="text-2xl text-blue-600 mb-3" />
            <p className="text-gray-800 font-medium mb-2">
              Falls Sie einen professionellen, zertifizierten Test durchführen möchten, stehen wir Ihnen gerne zur Verfügung.
            </p>
            <a href="tel:06921939559" className="text-xl font-bold text-blue-700 hover:text-blue-800 transition-colors">
              069-21939559
            </a>
          </div>
        </motion.div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9 }}
          className="mt-8 text-center text-sm text-gray-500"
        >
          <p>
            Dieses Assessment dient ausschließlich informativen Zwecken und ersetzt keine professionelle psychologische Beratung.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Results;